<template>
  <div class="agents-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Role Filter -->
        <div class="role-filter-container">
          <button @click="showAddAgentModal = true" class="add-agent-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            Add Agent
          </button>
          <select v-model="selectedRole" @change="loadAgents" class="role-filter">
            <option value="1">Customer Service Agents</option>
            <option value="2">Sales Agents</option>
          </select>
        </div>

        <!-- Stats Overview -->
        <div class="stats-overview">
          <div class="stat-card">
            <div class="stat-icon">👥</div>
            <div class="stat-content">
              <div class="stat-number">{{ agents.length }}</div>
              <div class="stat-label">Total {{ selectedRole === '1' ? 'Support' : 'Sales' }} Agents</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">✅</div>
            <div class="stat-content">
              <div class="stat-number">{{ activeAgents }}</div>
              <div class="stat-label">Active Agents</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">⭐</div>
            <div class="stat-content">
              <div class="stat-number">{{ averageSatisfaction }}%</div>
              <div class="stat-label">Avg. Satisfaction</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">⏱️</div>
            <div class="stat-content">
              <div class="stat-number">{{ averageHandleTime }}min</div>
              <div class="stat-label">Avg. Handle Time</div>
            </div>
          </div>
        </div>

        <!-- Agents Grid -->
        <div class="agents-grid">
          <div v-for="agent in agents" :key="agent.id" class="agent-card">
            <div class="agent-header">
              <div class="agent-info">
                <img :src="agent.emoji_url" :alt="agent.name" class="agent-emoji" />
                <div class="agent-details">
                  <h3 class="agent-name">{{ agent.name }}</h3>
                  <div class="agent-meta">
                    <button
                      class="agent-code clickable"
                      @click="showAgentQR(agent)"
                      title="Click to view QR code"
                    >
                      {{ agent.agent_code }}
                    </button>
                    <span class="agent-department">{{ agent.department_name }}</span>
                    <span class="agent-status" :class="{ active: agent.active, inactive: !agent.active }">
                      {{ agent.active ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Action buttons -->
              <div class="agent-actions">
                <button @click="setAgentPassword(agent)" class="action-btn password-btn" title="Set Password">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                </button>
                <button @click="generateReport(agent)" class="action-btn report-btn" title="Generate Report">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h8c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                  </svg>
                </button>
                <button @click="editAgent(agent)" class="action-btn edit-btn" title="Edit Agent">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                </button>
                <button @click="deleteAgent(agent)" class="action-btn delete-btn" title="Delete Agent">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="agent-metrics">
              <!-- Customer Satisfaction -->
              <div class="metric">
                <div class="metric-header">
                  <span class="metric-label">Customer Satisfaction</span>
                  <span class="metric-value">{{ agent.avg_customer_satisfaction_percent }}%</span>
                </div>
                <div class="progress-bar">
                  <div
                    class="progress-fill satisfaction"
                    :style="{ width: agent.avg_customer_satisfaction_percent + '%' }"
                  ></div>
                </div>
              </div>

              <!-- Agent Performance -->
              <div class="metric">
                <div class="metric-header">
                  <span class="metric-label">Agent Performance</span>
                  <span class="metric-value">{{ agent.avg_customer_service_agent_performance_percent }}%</span>
                </div>
                <div class="progress-bar">
                  <div
                    class="progress-fill performance"
                    :style="{ width: agent.avg_customer_service_agent_performance_percent + '%' }"
                  ></div>
                </div>
              </div>

              <!-- Problem Resolution -->
              <div class="metric">
                <div class="metric-header">
                  <span class="metric-label">Problem Resolution</span>
                  <span class="metric-value">{{ agent.avg_customer_effort_to_get_problem_fixed_percent }}%</span>
                </div>
                <div class="progress-bar">
                  <div
                    class="progress-fill effort"
                    :style="{ width: agent.avg_customer_effort_to_get_problem_fixed_percent + '%' }"
                  ></div>
                </div>
              </div>

              <!-- Handle Time Score -->
              <div class="metric">
                <div class="metric-header">
                  <span class="metric-label">Handle Time Score</span>
                  <span class="metric-value">{{ agent.avg_handle_time_score }}%</span>
                </div>
                <div class="handle-time-info">
                  <span class="handle-time">{{ agent.avg_handle_time }}min avg</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading agents...</p>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && agents.length === 0" class="empty-state">
          <div class="empty-icon">👥</div>
          <h3>No agents found</h3>
          <p>No {{ selectedRole === '1' ? 'customer service' : 'sales' }} agents are currently available.</p>
        </div>

        <!-- QR Code Modal -->
        <QRCodeModal
          :visible="showQRModal"
          :agent-code="selectedAgentCode"
          @close="showQRModal = false"
        />

        <!-- Edit Agent Modal -->
        <EditAgentModal
          :visible="showEditModal"
          :agent="selectedAgent"
          :departments="departments"
          @close="showEditModal = false"
          @save="handleEditAgent"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmModal
          :visible="showDeleteModal"
          :agent="selectedAgent"
          @close="showDeleteModal = false"
          @confirm="handleDeleteAgent"
        />

        <!-- Report Generation Modal -->
        <ReportGenerationModal
          :visible="showReportModal"
          :agent="selectedAgent"
          @close="showReportModal = false"
          @generate="handleGenerateReport"
        />

        <!-- Add Agent Modal -->
        <AddAgentModal
          :visible="showAddAgentModal"
          :departments="departments"
          @close="showAddAgentModal = false"
          @save="handleAddAgent"
        />

        <!-- Set Password Modal -->
        <div v-if="showPasswordModal" class="modal-overlay" @click.self="showPasswordModal = false">
          <div class="modal-content password-modal">
            <div class="modal-header">
              <h2>Set Agent Password</h2>
              <button class="close-btn" @click="showPasswordModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <p class="info-text">Set a password for <strong>{{ selectedAgent?.name }}</strong> ({{ selectedAgent?.agent_code }})</p>

              <div class="form-group">
                <label for="newPassword">New Password</label>
                <div class="password-input-wrapper">
                  <input
                    id="newPassword"
                    :type="showPasswordInput ? 'text' : 'password'"
                    v-model="newPassword"
                    placeholder="Enter password (min 8 characters)"
                    class="form-input"
                  />
                  <button
                    type="button"
                    class="toggle-password-btn"
                    @click="showPasswordInput = !showPasswordInput"
                  >
                    <svg v-if="!showPasswordInput" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  v-model="confirmPassword"
                  placeholder="Confirm password"
                  class="form-input"
                />
              </div>

              <div v-if="passwordError" class="error-message">
                {{ passwordError }}
              </div>

              <button
                class="generate-password-btn"
                @click="generateRandomPassword"
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
                Generate Random Password
              </button>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" @click="showPasswordModal = false">Cancel</button>
              <button class="btn btn-primary" @click="handleSetPassword" :disabled="settingPassword">
                {{ settingPassword ? 'Setting...' : 'Set Password' }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import QRCodeModal from '../components/QRCodeModal.vue'
import EditAgentModal from '../components/EditAgentModal.vue'
import DeleteConfirmModal from '../components/DeleteConfirmModal.vue'
import ReportGenerationModal from '../components/ReportGenerationModal.vue'
import AddAgentModal from '../components/AddAgentModal.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'AgentsView',
  components: {
    Sidebar,
    TopHeader,
    QRCodeModal,
    EditAgentModal,
    DeleteConfirmModal,
    ReportGenerationModal,
    AddAgentModal
  },
  data () {
    return {
      selectedRole: '1', // Default to Customer Service
      agents: [],
      departments: [],
      loading: false,
      // Modal states
      showQRModal: false,
      showEditModal: false,
      showDeleteModal: false,
      showReportModal: false,
      showAddAgentModal: false,
      showPasswordModal: false,
      selectedAgent: null,
      selectedAgentCode: '',
      // Password modal fields
      newPassword: '',
      confirmPassword: '',
      showPasswordInput: false,
      passwordError: '',
      settingPassword: false
    }
  },
  computed: {
    activeAgents () {
      return this.agents.filter(agent => agent.active).length
    },
    averageSatisfaction () {
      if (this.agents.length === 0) return 0
      const sum = this.agents.reduce((acc, agent) => acc + parseFloat(agent.avg_customer_satisfaction_percent || 0), 0)
      return Math.round(sum / this.agents.length)
    },
    averageHandleTime () {
      if (this.agents.length === 0) return 0
      const sum = this.agents.reduce((acc, agent) => acc + parseFloat(agent.avg_handle_time || 0), 0)
      return Math.round(sum / this.agents.length * 10) / 10
    }
  },
  async mounted () {
    await Promise.all([
      this.loadAgents(),
      this.loadDepartments()
    ])
  },
  methods: {
    async loadAgents () {
      try {
        this.loading = true
        this.agents = await DashboardService.getAgents(parseInt(this.selectedRole))
      } catch (error) {
        console.error('Error loading agents:', error)
        this.agents = []
      } finally {
        this.loading = false
      }
    },

    async loadDepartments () {
      try {
        const result = await DashboardService.getDepartments()
        if (result.success) {
          this.departments = result.departments
        } else {
          console.error('Failed to load departments:', result.error)
          this.departments = []
        }
      } catch (error) {
        console.error('Error loading departments:', error)
        this.departments = []
      }
    },

    // QR Code functionality
    showAgentQR (agent) {
      this.selectedAgentCode = agent.agent_code
      this.showQRModal = true
    },

    // Report generation
    generateReport (agent) {
      this.selectedAgent = { ...agent }
      this.showReportModal = true
    },

    async handleGenerateReport (data) {
      try {
        this.showReportModal = false

        // Navigate to the new agent report page
        const routeQuery = {
          period: 'custom',
          date_from: data.startDate,
          date_to: data.endDate
        }

        // Open report in new window/tab
        const routeUrl = this.$router.resolve({
          name: 'agent-report',
          params: { agentId: data.agentId },
          query: routeQuery
        })

        window.open(routeUrl.href, '_blank')
      } catch (error) {
        console.error('Error opening report:', error)
        alert('Failed to open report: ' + error.message)
      }
    },

    // Edit agent functionality
    editAgent (agent) {
      this.selectedAgent = { ...agent }
      this.showEditModal = true
    },

    async handleEditAgent (data) {
      try {
        const result = await DashboardService.editAgent(data.agentId, data.name, data.departmentId)

        if (result.success) {
          this.showEditModal = false
          await this.loadAgents() // Refresh agents list
          alert(result.message || 'Agent updated successfully')
        } else {
          throw new Error(result.error || 'Failed to update agent')
        }
      } catch (error) {
        console.error('Error updating agent:', error)
        alert('Failed to update agent: ' + error.message)
      }
    },

    // Delete agent functionality
    deleteAgent (agent) {
      this.selectedAgent = { ...agent }
      this.showDeleteModal = true
    },

    async handleDeleteAgent (agentId) {
      try {
        const result = await DashboardService.deleteAgent(agentId)

        if (result.success) {
          this.showDeleteModal = false
          await this.loadAgents() // Refresh agents list
          alert(result.message || 'Agent deleted successfully')
        } else {
          throw new Error(result.error || 'Failed to delete agent')
        }
      } catch (error) {
        console.error('Error deleting agent:', error)
        alert('Failed to delete agent: ' + error.message)
      }
    },

    // Add agent functionality
    async handleAddAgent (data) {
      try {
        const result = await DashboardService.addAgent(data.name, data.departmentId, data.agentRoleId)

        if (result.success) {
          this.showAddAgentModal = false
          await this.loadAgents() // Refresh agents list
          alert(result.message || 'Agent created successfully')
        } else {
          throw new Error(result.error || 'Failed to create agent')
        }
      } catch (error) {
        console.error('Error creating agent:', error)
        alert('Failed to create agent: ' + error.message)
      }
    },

    // Set password functionality
    setAgentPassword (agent) {
      this.selectedAgent = { ...agent }
      this.newPassword = ''
      this.confirmPassword = ''
      this.showPasswordInput = false
      this.passwordError = ''
      this.showPasswordModal = true
    },

    generateRandomPassword () {
      const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
      let password = ''
      for (let i = 0; i < 12; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length))
      }
      this.newPassword = password
      this.confirmPassword = password
      this.showPasswordInput = true
    },

    async handleSetPassword () {
      // Validate
      this.passwordError = ''

      if (!this.newPassword) {
        this.passwordError = 'Please enter a password'
        return
      }

      if (this.newPassword.length < 8) {
        this.passwordError = 'Password must be at least 8 characters'
        return
      }

      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = 'Passwords do not match'
        return
      }

      this.settingPassword = true

      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/agents/set-password', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            agent_id: this.selectedAgent.id,
            password: this.newPassword
          })
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || 'Failed to set password')
        }

        if (data.success) {
          this.showPasswordModal = false
          alert(`Password set successfully for ${this.selectedAgent.name}!\n\nAgent Code: ${this.selectedAgent.agent_code}\nPassword: ${this.newPassword}\n\nPlease share these credentials with the agent securely.`)
        } else {
          throw new Error(data.message || 'Failed to set password')
        }
      } catch (error) {
        console.error('Error setting password:', error)
        this.passwordError = error.message
      } finally {
        this.settingPassword = false
      }
    }
  }
}
</script>

<style scoped>
.agents-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.role-filter-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.add-agent-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: #3b82f6;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-agent-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.role-filter {
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  min-width: 200px;
  transition: all 0.3s ease;
}

.role-filter:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.role-filter option {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.stats-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.stat-icon {
  font-size: 32px;
  opacity: 0.8;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
}

.agent-card {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  transition: all 0.2s;
}

.agent-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px var(--shadow-primary);
}

.agent-header {
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-emoji {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--bg-secondary);
  padding: 4px;
}

.agent-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.agent-meta {
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 12px;
}

.agent-code {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}

.agent-department {
  color: var(--text-secondary);
}

.agent-status {
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.agent-status.active {
  background: rgba(22, 101, 52, 0.1);
  color: #16a34a;
}

.agent-status.inactive {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Dark mode specific styling for agent status */
.dark .agent-status.active {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}

.dark .agent-status.inactive {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.agent-metrics {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.metric-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.metric-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.progress-bar {
  height: 8px;
  background: var(--bg-secondary);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-fill.satisfaction {
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
}

.progress-fill.performance {
  background: linear-gradient(90deg, #10b981, #059669);
}

.progress-fill.effort {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.handle-time-info {
  text-align: center;
  padding: 8px;
  background: var(--bg-secondary);
  border-radius: 6px;
}

.handle-time {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    padding: 20px 16px;
  }

  .stats-overview {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .stat-card {
    padding: 16px;
    gap: 12px;
  }

  .stat-icon {
    font-size: 24px;
  }

  .stat-number {
    font-size: 20px;
  }

  .agents-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .agent-card {
    padding: 16px;
  }
}

/* Action buttons */
.agent-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.report-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.edit-btn:hover {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
}

.delete-btn {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Dark mode specific styling for action buttons */
.dark .report-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.dark .edit-btn:hover {
  background: rgba(139, 92, 246, 0.2);
  color: #a855f7;
}

.dark .delete-btn {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.delete-btn:hover {
  background: #ef4444;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

/* Clickable agent code */
.agent-code.clickable {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.agent-code.clickable:hover {
  background: #3b82f6;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

@media (max-width: 768px) {
  .agent-actions {
    flex-direction: column;
    gap: 4px;
  }

  .action-btn {
    width: 32px;
    height: 32px;
  }
}

/* Password button styling */
.password-btn:hover {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.dark .password-btn:hover {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

/* Password Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.info-text {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.info-text strong {
  color: var(--text-primary);
  font-weight: 600;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  padding-right: 48px;
}

.toggle-password-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toggle-password-btn:hover {
  color: var(--text-primary);
}

.error-message {
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
  margin-bottom: 16px;
}

.generate-password-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.generate-password-btn:hover {
  background: var(--bg-tertiary);
  border-color: #3b82f6;
  color: #3b82f6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-primary);
}

.btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
